/**
 * programOnboarding/flags — the two feature flags for this feature, readable
 * from BOTH the parent app (web-app) and the child app (web-child; iOS/Android
 * via @child). Because web-app cannot import web-child at runtime, the flag
 * READER lives here in ui-shared, over the shared `createSignedFlag` primitive
 * (../signedFlag.ts) that uses the EXACT same signed-envelope scheme as
 * web-child/flagGuard.ts (same gate-token key, same fnv1a, same
 * `fnv1a(key|value|token)` signature) so the existing ops hook
 * `window.__lwaRigorFlags.set(...)` writes an envelope this reader accepts.
 *
 * Both flags default ON as of the 2026-08-04 owner-directed release (see
 * DEFAULT_ON below). A bare/legacy/foreign localStorage write is still ignored —
 * only a correctly parent-signed `{ v:'off', sig }` envelope can disable a flag
 * per-device. Fleet rollback = revert the DEFAULT_ON change + redeploy. See
 * design §8 flag matrix + §14-F.
 *
 * READ-ONLY here by design: writes go through the parent-side ops hook /
 * flagGuard.setRigorFlag (these keys are in flagGuard's GUARDED_FLAGS), so this
 * module intentionally exposes no setter or test seam.
 */

import { createSignedFlag } from '../signedFlag';

export const PROGRAM_CHOICE_FLAG = 'lwa_program_choice_v1';
export const READING_CHECK_FLAG = 'lwa_foundations_reading_check_v1';
export type ProgramOnboardingFlag = typeof PROGRAM_CHOICE_FLAG | typeof READING_CHECK_FLAG;

/**
 * ENABLED 2026-08-04 (owner-directed live release). Both flags now default ON:
 * an absent key reads true; a parent-signed `{ v:'off' }` envelope still disables
 * per-device. Kept in lockstep with web-child/flagGuard.ts (these two are NO
 * LONGER in its DEFAULT_OFF_FLAGS). Rollback = move a flag back out of DEFAULT_ON
 * here + re-add it to flagGuard's DEFAULT_OFF_FLAGS + redeploy (one revert).
 */
const DEFAULT_ON: readonly ProgramOnboardingFlag[] = [PROGRAM_CHOICE_FLAG, READING_CHECK_FLAG];

/** One signed-flag reader per key, defaulting per DEFAULT_ON above. */
const readers: Record<ProgramOnboardingFlag, ReturnType<typeof createSignedFlag>> = {
  [PROGRAM_CHOICE_FLAG]: createSignedFlag(PROGRAM_CHOICE_FLAG, { defaultOn: DEFAULT_ON.includes(PROGRAM_CHOICE_FLAG) }),
  [READING_CHECK_FLAG]: createSignedFlag(READING_CHECK_FLAG, { defaultOn: DEFAULT_ON.includes(READING_CHECK_FLAG) }),
};

/**
 * Read a program-onboarding flag. Default ON (2026-08-04 release). Returns the
 * default for every shape except a correctly parent-signed `{ v, sig }` envelope
 * (absent, bare 'on'/'off'/'1'/'true', malformed, wrong/missing signature all
 * read the default), so a bare `key='off'` write can never disable a default-ON
 * flag. Never throws.
 */
export function isProgramFlagOn(key: ProgramOnboardingFlag): boolean {
  return readers[key].isEnabled();
}

/** Program choice (parent flow + server program memory + renamed home/settings). */
export function isProgramChoiceEnabled(): boolean {
  return isProgramFlagOn(PROGRAM_CHOICE_FLAG);
}

/** Foundations Reading Check (replaces the legacy 12-level placement). */
export function isReadingCheckEnabled(): boolean {
  return isProgramFlagOn(READING_CHECK_FLAG);
}
